// @flow

import type {RequestParameters} from '../util/ajax';
import {makeRequest} from '../util/ajax';
import urls from '../util/urls';
import config from '../util/config';

// https://developer.trimblemaps.com/restful-apis/places/introduction/
type PlaceOptions = {
    token: string,
    placeId: string,
}

/**
 * Places service
 */
export const Places = {
    /**
     * Gets authentication token for the api key.
     *
     * @returns Promise<string>
     *
     * @example
     * const authPromise = TrimbleMaps.Places.authenticate();
     * authPromise.then((resp) => {
     *   const token = JSON.parse(resp).token;
     * });
     */
    authenticate,

    /**
     * Gets the standard information about a single place.
     * @param {PlaceOptions} options Options
     * @param {string} [options.token] Authentication token. Required.
     * @param {string} [options.placeId] Place Id. Required.
     *
     * @returns Promise<any>
     *
     * @example
     * const placePromise = TrimbleMaps.Places.getPlace({
     *   token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9',
     *   placeId: '0xlM0l28x0LEiCiOPrHt-_WQ'
     * });
     */
    getPlace,

    /**
     * Gets the full details about a single place.
     * @param {PlaceOptions} options Options
     * @param {string} [options.token] Authentication token. Required.
     * @param {string} [options.placeId] Place Id. Required.
     *
     * @returns Promise<any>
     *
     * @example
     * const placeDetailsPromise = TrimbleMaps.Places.getPlaceDetails({
     *   token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9',
     *   placeId: '0xlM0l28x0LEiCiOPrHt-_WQ'
     * });
     */
    getPlaceDetails,
};

/**
 * @private
 *
 * Gets JWT token for the apikey.
 *
 * @returns Promise<string>
 */
function authenticate(): Promise<string> {
    // For user based authentication, POST authws.alk.com, it is out of scope of the SDK for now.
    // POST 'https://api-qa.trimblemaps.com/places/v1/authenticate'
    const url = `${urls.AUTH_URL}`;
    const request: RequestParameters = {
        url,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body:JSON.stringify({
            apiKey: config.API_KEY
        })
    };
    return new Promise((resolve) => {
        makeRequest(request, (_, resp: any) => {
            resolve(resp);
        });
    });
}
/**
 * @private
 *
 * Gets the standard information about a single place.
 * @param {PlaceOptions} options Options
 *
 * @returns Promise<any>
 */
function getPlace(options: PlaceOptions): Promise<any> {
    options = options || {};
    const placeId = options.placeId;
    const token = options.token || config.API_TOKEN;
    if (placeId === undefined || placeId === null || placeId.length === 0) {
        throw new Error('Place Id is required.');
    }
    if (token === undefined || token === null || token.length === 0) {
        throw new Error('Authentication token is required.');
    }
    // GET 'https://api-qa.trimblemaps.com/places/v1/place'
    const url = `${urls.PLACES_URL}/place/${placeId}`;
    const request: RequestParameters = {
        url,
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `bearer ${token}`
        }
    };
    return new Promise((resolve) => {
        makeRequest(request, (_, resp: any) => {
            resolve(resp);
        });
    });
}

/**
 * @private
 *
 * Gets the full details about a single place.
 * @param {PlaceOptions} options Options
 *
 * @returns Promise<any>
 */
function getPlaceDetails(options: PlaceOptions): Promise<any> {
    options = options || {};
    const placeId = options.placeId;
    const token = options.token || config.API_TOKEN;
    if (placeId === undefined || placeId === null || placeId.length === 0) {
        throw new Error('Place Id is required.');
    }
    if (token === undefined || token === null || token.length === 0) {
        throw new Error('Authentication token is required.');
    }
    // GET 'https://api-qa.trimblemaps.com/places/v1/place'
    const url = `${urls.PLACES_URL}/place/${placeId}/details`;
    const request: RequestParameters = {
        url,
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization':  `bearer ${token}`
        }
    };
    return new Promise((resolve) => {
        makeRequest(request, (_, resp: any) => {
            resolve(resp);
        });
    });
}
